import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { type GetProps, getTokenValue, styled, type Token, View } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { hairline } from "../utils";
import { Interface2, Interface3 } from "./Typography";
export type BadgeVariant = "outlined" | "filled";
export type BadgeSize = "small" | "medium";
type Status = "neutral" | "positive" | "negative" | "warning" | "info";

// Keep text and icon tokens distinct even when their values match today —
// they're semantically different and may diverge.
const badgeStatusTokens = {
  neutral: {
    bg: "$surface0",
    border: "$borderStrong",
    text: "$textDefault",
    icon: "$iconDefault"
  },
  positive: {
    bg: "$bgPositive",
    border: "$borderPositiveStrong",
    text: "$textPositive",
    icon: "$iconPositive"
  },
  negative: {
    bg: "$bgNegative",
    border: "$borderNegativeStrong",
    text: "$textNegative",
    icon: "$iconNegative"
  },
  warning: {
    bg: "$bgWarning",
    border: "$borderWarningStrong",
    text: "$textWarning",
    icon: "$iconWarning"
  },
  info: {
    bg: "$bgAccent",
    border: "$borderAccentStrong",
    text: "$textAccent",
    icon: "$iconAccent"
  }
} as const satisfies Record<Status, {
  bg: string;
  border: string;
  text: string;
  icon: string;
}>;
const badgeVariants = {
  status: {
    neutral: {
      backgroundColor: badgeStatusTokens.neutral.bg,
      borderColor: badgeStatusTokens.neutral.border
    },
    positive: {
      backgroundColor: badgeStatusTokens.positive.bg,
      borderColor: badgeStatusTokens.positive.border
    },
    negative: {
      backgroundColor: badgeStatusTokens.negative.bg,
      borderColor: badgeStatusTokens.negative.border
    },
    warning: {
      backgroundColor: badgeStatusTokens.warning.bg,
      borderColor: badgeStatusTokens.warning.border
    },
    info: {
      backgroundColor: badgeStatusTokens.info.bg,
      borderColor: badgeStatusTokens.info.border
    }
  },
  variant: {
    outlined: {
      backgroundColor: "$transparent",
      borderWidth: hairline
    },
    filled: {
      borderWidth: 0
    }
  },
  size: {
    medium: {
      height: "$1.5",
      paddingHorizontal: "$sp1"
    },
    small: {
      height: "$1",
      paddingHorizontal: "$sp0.5"
    }
  }
} as const;
const StyledBadge = styled(View, {
  name: "Badge",
  render: "span",
  role: "status",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  gap: "$sp0.5",
  overflow: "hidden",
  borderRadius: "$brFull",
  variants: badgeVariants,
  defaultVariants: {
    status: "neutral",
    variant: "filled",
    size: "medium"
  }
});
const getIconSizeToken = (size: BadgeSize): Token => `$${size === "small" ? "iconXSmall" : "iconSmall"}`;
type StyledBadgeProps = GetProps<typeof StyledBadge>;
export type BadgeProps = Omit<StyledBadgeProps, "children"> & {
  status?: Status;
  variant?: BadgeVariant;
  size?: BadgeSize;
  iconLeft?: FunctionComponent<IconProps>;
  iconRight?: FunctionComponent<IconProps>;
  children: ReactNode;
};
const truncationStyles = {
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  style: {
    overflowX: "clip",
    overflowY: "visible",
    minWidth: 0
  }
} as const;
const BadgeTextMedium = styled(Interface2, truncationStyles);
const BadgeTextSmall = styled(Interface3, truncationStyles);
export const Badge = (props: BadgeProps) => {
  const {
    status = "neutral",
    variant = "filled",
    size = "medium",
    iconLeft: IconLeft,
    iconRight: IconRight,
    children,
    ...rest
  } = props;
  const iconSize = useMemo(() => getTokenValue(getIconSizeToken(size), "size") as number, [size]);
  const tokens = badgeStatusTokens[status];
  const BadgeText = size === "small" ? BadgeTextSmall : BadgeTextMedium;
  return <StyledBadge {...rest} status={status} variant={variant} size={size} data-sentry-element="StyledBadge" data-sentry-component="Badge" data-sentry-source-file="Badge.tsx">
      {IconLeft ? <View flexShrink={0}>
          <IconLeft size={iconSize} color={tokens.icon} />
        </View> : null}
      <BadgeText color={tokens.text} flexShrink={1} numberOfLines={1} data-sentry-element="BadgeText" data-sentry-source-file="Badge.tsx">
        {children}
      </BadgeText>
      {IconRight ? <View flexShrink={0}>
          <IconRight size={iconSize} color={tokens.icon} />
        </View> : null}
    </StyledBadge>;
};